/* eslint-disable no-shadow */
import {
  getToolIds,
  isAssignmentInProgress,
} from '@macmillan-learning/gradebook-shared';
import debounce from 'lodash/debounce';
import difference from 'lodash/difference';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import slice from 'lodash/slice';
import _toNumber from 'lodash/toNumber';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ArrowKeyStepper, AutoSizer, MultiGrid } from 'react-virtualized';
import AccessibleGridCellWrapper from '../Components/AccessibleGrid/AccessibleGridCellWrapper';
import 'react-virtualized/styles.css';

import { exceptionGroupsStudentIdsSelector } from '../../../selectors/search-gradebook';
import {
  selectGradingType,
  selectRoundPercentType,
} from '../../../selectors/settings';
import { getConfigObject } from '../../../utils/config';
import {
  AGGRATE_COLUMN_WIDTH,
  ASSIGNMENT_ROW,
  ASSIGNMENT_ROW_HEIGHT,
  ASSIGNMENT_ROW_HEIGHT_TALLER,
  ASSIGNMENT_ROW_HEIGHT_WITH_EXCEPTIONS,
  CATEGORY_ROW,
  CATEGORY_ROW_HEIGHT,
  CATEGORY_TOTAL_COLUMN_WIDTH,
  COURSE_TOTAL_COLUMN_WIDTH,
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_ROW_HEIGHT,
  FIRST_COLUMN,
  FIXED_COLUMN_COUNT_SIDE_PANEL_ACTIVE,
  HEADER_ROWS_COUNT,
  SEARCH_TYPES,
  STUDENT_COLUMN_WIDTH,
} from '../../../utils/constants';
import EmptyGradebook from '../Components/EmptyGradebook';
import GridCell from '../Components/GridCell';
import NoSearchResultsGradebook from '../Components/NoSearchResultsGradebook';
import s from './InstructorGradebookGrid.module.css';

class InstructorGradebookGrid extends Component {
  static propTypes = {
    hasGradebookFetched: PropTypes.bool,
    courseId: PropTypes.string.isRequired,
    gradebookGridData: PropTypes.shape({
      rows: PropTypes.array.isRequired,
      rowCount: PropTypes.number.isRequired,
      columnCount: PropTypes.number.isRequired,
      leftGridColumnCount: PropTypes.number.isRequired,
    }),
    collapsedCategoryIds: PropTypes.array.isRequired,
    showPercent: PropTypes.bool,
    isGradebookFetching: PropTypes.bool.isRequired,
    isGradebookReFetching: PropTypes.bool.isRequired,
    gradebookTableHeight: PropTypes.number,
    showGradeOverrideDialog: PropTypes.func,
    searchPhrase: PropTypes.string.isRequired,
    searchType: PropTypes.string.isRequired,
    updateAssignmentAverages: PropTypes.func,
    averages: PropTypes.array,
    instructor: PropTypes.object,
    updateCell: PropTypes.func,
    updateScrollPosition: PropTypes.func.isRequired,
    scrollLeft: PropTypes.number,
    scrollTop: PropTypes.number,
    gradingType: PropTypes.number,
    roundPercentType: PropTypes.number,
    user: PropTypes.object,
    onTabChange: PropTypes.func,
    getLaunchUrl: PropTypes.func,
    handleLocalLaunch: PropTypes.func,
    hasStudentReport: PropTypes.bool,
    getStudentReportLink: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.scrollInfo = {
      scrollTop: 0,
      scrollLeft: 0,
    };
    this.gridDefaults = {
      fixedRowCount: 3,
      enableFixedColumnScroll: true,
      enableFixedRowScroll: true,
    };
    this.gridRef = React.createRef();
  }

  static defaultProps = {
    onTabChange: () => {},
    getStudentReportLink: () => {},
  };

  componentDidMount() {
    this.removeDoubleScrollBars();
    this.recomputeAverages = true;
  }

  componentDidUpdate = () => {
    if (this.gridRef.current) {
      this.gridRef.current.forceUpdateGrids();
      this.gridRef.current.recomputeGridSize();
    }
  };

  componentWillUnmount() {
    this.props.updateScrollPosition(this.scrollInfo);
    this.hasRendered = false;
  }

  removeDoubleScrollBars = () => {
    window.dispatchEvent(new Event('resize'));
  };

  cellRenderer = ({
    key,
    style,
    columnIndex,
    rowIndex,
    scrollToColumn,
    scrollToRow,
  }) => {
    const {
      courseId,
      user,
      gradingType,
      roundPercentType,
      showPercent,
      collapsedCategoryIds,
      showGradeOverrideDialog,
      gradebookGridData: { rowCount, leftGridColumnCount, hasStudents },
      getLaunchUrl,
      handleLocalLaunch,
      hasStudentReport,
      getStudentReportLink,
    } = this.props;
    const { hoveredColumnIndex, hoveredRowIndex } =
      this.props.instructor.gradebook;
    const row = get(this.props, `gradebookGridData.rows.[${rowIndex}]`);
    const cellData = row && get(row, `[${columnIndex}]`);

    // Only render the wrapper for the first cell in each row
    // Other cells will be children of this row element
    if (columnIndex === 0) {
      return (
        <AccessibleGridCellWrapper
          key={key}
          rowIndex={rowIndex}
          style={{
            ...style,
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
          }}
        >
          <GridCell
            key={`${key}-cell`}
            style={{ ...style, position: 'static' }}
            courseId={courseId}
            cellData={cellData}
            columnIndex={columnIndex}
            gradingType={gradingType}
            roundPercentType={roundPercentType}
            rowIndex={rowIndex}
            rowCount={rowCount}
            showPercent={showPercent && hasStudents}
            scrollToColumn={scrollToColumn}
            scrollToRow={scrollToRow}
            onSelectCell={this.selectCell}
            handleCellHover={this.handleCellHover}
            hoveredRowIndex={hoveredRowIndex}
            hoveredColumnIndex={hoveredColumnIndex}
            courseExceptions={[]}
            leftGridColumnCount={leftGridColumnCount}
            collapsedCategoryIds={collapsedCategoryIds}
            showGradeOverrideDialog={showGradeOverrideDialog}
            user={user}
            getLaunchUrl={getLaunchUrl}
            handleLocalLaunch={handleLocalLaunch}
            hasStudentReport={hasStudentReport}
            getStudentReportLink={getStudentReportLink}
          />
        </AccessibleGridCellWrapper>
      );
    }

    // For other cells, render them normally
    return (
      <GridCell
        key={key}
        style={style}
        courseId={courseId}
        cellData={cellData}
        columnIndex={columnIndex}
        gradingType={gradingType}
        roundPercentType={roundPercentType}
        rowIndex={rowIndex}
        rowCount={rowCount}
        showPercent={showPercent && hasStudents}
        scrollToColumn={scrollToColumn}
        scrollToRow={scrollToRow}
        onSelectCell={this.selectCell}
        handleCellHover={this.handleCellHover}
        hoveredRowIndex={hoveredRowIndex}
        hoveredColumnIndex={hoveredColumnIndex}
        courseExceptions={[]}
        leftGridColumnCount={leftGridColumnCount}
        collapsedCategoryIds={collapsedCategoryIds}
        showGradeOverrideDialog={showGradeOverrideDialog}
        user={user}
        getLaunchUrl={getLaunchUrl}
        handleLocalLaunch={handleLocalLaunch}
        hasStudentReport={hasStudentReport}
        getStudentReportLink={getStudentReportLink}
      />
    );
  };

  selectCell = ({ scrollToColumn, scrollToRow }) => {
    this.props.updateCell({ scrollToColumn, scrollToRow });
  };

  handleCellHover = (colRowHoverPayload) => {
    this.props.updateCell(colRowHoverPayload);
  };

  renderGradebook = () => {
    const {
      gradebookTableHeight,
      gradebookGridData: {
        rows,
        leftGridColumnCount,
        rowCount: studentRowCount,
        hasStudents,
      },
      scrollLeft,
      scrollTop,
      updateAssignmentAverages,
      averages,
    } = this.props;

    const { FF_SIDE_PANEL_ACTIVE } = getConfigObject();
    const totalRows = studentRowCount + HEADER_ROWS_COUNT;
    const {
      scrollToColumn: scrollToColumnState,
      scrollToRow: scrollToRowState,
    } = this.props.instructor.gradebook;
    const columnCount = get(rows, '[0].length', 0);
    const gridConfig = {
      ...this.gridDefaults,
      fixedColumnCount:
        FF_SIDE_PANEL_ACTIVE && hasStudents
          ? FIXED_COLUMN_COUNT_SIDE_PANEL_ACTIVE
          : leftGridColumnCount,
    };
    const assignmentsRow = get(rows, '[1]', []);
    // this allow ensure ever will have a correct padding
    const someException = assignmentsRow.some(
      (assignmentRow) =>
        assignmentRow.Exceptions && assignmentRow.Exceptions.length,
    );
    const someExceptionInProgress = assignmentsRow.some(
      (assignmentRow) =>
        assignmentRow.tool !== getToolIds().manual &&
        isAssignmentInProgress(assignmentRow) &&
        assignmentRow.Exceptions &&
        assignmentRow.Exceptions.length,
    );

    let realScrollLeft;
    let realScrollTop;
    if (this.hasRendered) {
      realScrollLeft = undefined;
      realScrollTop = undefined;
    } else {
      this.hasRendered = true;
      realScrollLeft = scrollLeft;
      realScrollTop = scrollTop;
    }

    return (
      <ArrowKeyStepper
        isControlled
        mode="cells"
        rowCount={totalRows}
        columnCount={columnCount}
        onScrollToChange={this.selectCell}
        scrollToColumn={scrollToColumnState}
        scrollToRow={scrollToRowState}
      >
        {({ scrollToColumn, scrollToRow }) => (
          <AutoSizer disableHeight>
            {({ width }) => (
              <MultiGrid
                {...gridConfig}
                width={width}
                ref={this.gridRef}
                height={gradebookTableHeight}
                classNameTopRightGrid={s.topRightGrid}
                classNameBottomLeftGrid={s.bottomLeftGrid}
                classNameBottomRightGrid={hasStudents ? '' : s.bottomRightGrid}
                columnCount={columnCount}
                rowCount={totalRows}
                estimatedColumnSize={DEFAULT_COLUMN_WIDTH}
                aria-rowcount={totalRows}
                aria-colcount={columnCount}
                role="grid"
                columnWidth={({ index }) => {
                  const isAggregateColumn = get(
                    assignmentsRow,
                    `[${index}].isAggregateCell`,
                    false,
                  );
                  const gradingType = get(
                    assignmentsRow,
                    `[${index}].grading_type`,
                  );
                  const gradebookCategoryId = get(
                    assignmentsRow,
                    `[${index}].gradebook_category_id`,
                  );
                  const isCourseTotalColumn = get(
                    assignmentsRow,
                    `[${index}].isCourseTotalColumn`,
                    false,
                  );
                  const id = get(assignmentsRow, `[${index}].id`, '');

                  if (isCourseTotalColumn) return COURSE_TOTAL_COLUMN_WIDTH;
                  if (
                    (!gradingType && gradebookCategoryId === -1) ||
                    (_toNumber(id) === gradebookCategoryId &&
                      gradebookCategoryId !== -1)
                  )
                    return CATEGORY_TOTAL_COLUMN_WIDTH;
                  if (isAggregateColumn) return AGGRATE_COLUMN_WIDTH;

                  switch (index) {
                    case FIRST_COLUMN:
                      return hasStudents
                        ? STUDENT_COLUMN_WIDTH
                        : DEFAULT_COLUMN_WIDTH;
                    default:
                      return DEFAULT_COLUMN_WIDTH;
                  }
                }}
                onScroll={({ scrollTop, scrollLeft }) => {
                  this.scrollInfo = { scrollTop, scrollLeft };
                }}
                scrollLeft={realScrollLeft}
                scrollTop={realScrollTop}
                rowHeight={({ index }) => {
                  switch (index) {
                    case CATEGORY_ROW:
                      return CATEGORY_ROW_HEIGHT;
                    case ASSIGNMENT_ROW:
                      if (someExceptionInProgress)
                        return ASSIGNMENT_ROW_HEIGHT_TALLER;

                      return someException
                        ? ASSIGNMENT_ROW_HEIGHT_WITH_EXCEPTIONS
                        : ASSIGNMENT_ROW_HEIGHT;
                    default:
                      return DEFAULT_ROW_HEIGHT;
                  }
                }}
                onSectionRendered={debounce((res) => {
                  const row = get(this.props, 'gradebookGridData.rows.[0]', []);
                  const numStickyHeaderColumns = 2;
                  const visibleAssignmentIds = map(
                    filter(
                      slice(
                        row,
                        res.columnStartIndex + numStickyHeaderColumns,
                        res.columnStopIndex + numStickyHeaderColumns + 1,
                      ),
                      (d) => !d.isAggregateCell && d.id,
                    ),
                    'id',
                  );
                  const assignmentIds = difference(
                    visibleAssignmentIds,
                    map(averages, 'assignmentId'),
                  );
                  if (!isEmpty(assignmentIds)) {
                    updateAssignmentAverages(assignmentIds);
                  } else if (this.recomputeAverages) {
                    updateAssignmentAverages(visibleAssignmentIds);
                    this.recomputeAverages = false;
                  }
                }, 400)}
                cellRenderer={({ columnIndex, key, rowIndex, style }) =>
                  this.cellRenderer({
                    key,
                    style,
                    columnIndex,
                    rowIndex,
                    scrollToColumn,
                    scrollToRow,
                  })
                }
              />
            )}
          </AutoSizer>
        )}
      </ArrowKeyStepper>
    );
  };

  render() {
    const {
      searchPhrase,
      searchType,
      onTabChange,
      hasGradebookFetched,
      isGradebookReFetching,
      gradebookGridData: { hasAssignments, hasStudents },
    } = this.props;

    const isFetchedDefaultGradebookWithoutAssignments =
      hasGradebookFetched &&
      !searchPhrase &&
      !isGradebookReFetching &&
      !hasAssignments;
    if (isFetchedDefaultGradebookWithoutAssignments) {
      return <EmptyGradebook type={'assignments'} onTabChange={onTabChange} />;
    }

    // TODO: [RNI-5406] Add loading state going from no results -> results
    const isStudentSearchResultsEmpty =
      (searchPhrase || isGradebookReFetching) &&
      !hasStudents &&
      searchType === SEARCH_TYPES.STUDENT;
    if (isStudentSearchResultsEmpty) {
      return (
        <NoSearchResultsGradebook searchPhrase={this.props.searchPhrase} />
      );
    }

    return this.renderGradebook();
  }
}

function mapStateToProps(state) {
  return {
    hasGradebookFetched: state.gradebook.hasGradebookFetched,
    averages: state.gradebook.averages,
    exceptionGroupsStudentIds: exceptionGroupsStudentIdsSelector(state),
    gradingType: selectGradingType(state),
    roundPercentType: selectRoundPercentType(state),
  };
}

export default connect(mapStateToProps)(InstructorGradebookGrid);
